/**
 * @file EditExtractedInformation.tsx
 *
 * @description This component is a comprehensive form interface for police officers
 * to review and edit extracted data from video evidence. It provides a multi-section
 * form with validation, field formatting, and confirmation workflows to ensure accurate data collection.
 *
 * @module Components
 * @requires Material-UI
 * @requires React
 * @requires axios
 */

import {
  Box,
  Typography,
  Divider,
  CircularProgress,
  Fade,
  Paper,
  Chip,
  Alert,
  Skeleton,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import { useState, useRef, useEffect, useMemo } from "react";
import { VideoPreview } from "../../components/video/VideoPreview";
import { useToast } from "../../shared/toast";
import { generateUiSchema } from "@/features/reports/utils/generateUiSchema";
import { getObjectSchemaAndData } from "@/features/reports/utils/getObjectSchemaAndData";
import { AddButton, RemoveButton } from "../../components/jsrfTemplates";
import { SchemaForm } from "../../shared/ui";
import { useFormDataStore } from "./store/formDataStore";

import { useEditSection } from "./EditSectionContext";
import {
  initializeNullArrays,
  getReportInformationProperties,
} from "./formUtils";
import { useSectionNavigation, useFormSections } from "./hooks";
import {
  SectionProgressBar,
  FormNavigation,
  FormSubmitArea,
  ConfirmationCheckbox,
} from "./components";
import { formatSchemaName } from "@/shared/utils/formatSchemaName";
import { RJSFSchema, UiSchema } from "@rjsf/utils";
import { IChangeEvent } from "@rjsf/core";
import type { JSONSchema7 } from "json-schema";
import type { FieldMapping } from "@/features/reports/config/fieldMapping";
import { useOrgId } from "../../shared/auth/orgContext";

/**
 * @name EditExtractedInformation
 *
 * @description Component for editing information extracted from law enforcement video evidence
 *
 * This component provides a multi-section form interface for officers to review and modify
 * automatically extracted information from video evidence. It includes features like:
 * - Section navigation with progress tracking
 * - Array item management (add/delete/navigate)
 * - Field validation based on model schema
 * - Confirmations for each section and final submission
 * - Undo functionality for changes (section-specific or all changes)
 * - Special handling for narrative text with "easter egg" validation
 *
 * @returns {JSX.Element} A comprehensive form interface to edit extracted report information.
 */
export const EditExtractedInformation: React.FC = () => {
  // Everything the page used to drill through 21 props now comes from the
  // EditSectionProvider; form data is read straight from the zustand store
  // (the page renders this component only once the store is initialized).
  const {
    report: {
      reportId,
      reportType,
      allowEditing,
      isReportOwner,
    },
    schema: { jsonSchema: jsonSchemaOrNull, schemaMapping: schemaMappingOrNull },
    video: { data: videoData, loadStatus: videoLoadStatus },
    save: {
      isSaving,
      updateFormData: onUpdate,
      saveChanges: handleSaveChanges,
      submit: onSubmit,
    },
    session: { liveWebSocket, logUserActivity: handleUserActivity },
    agent: { refreshReportData: fetchReportData, formDataSnapshotRef },
    chat: { sendMessageRef: chatSendMessageRef, setShowChat },
    userProfile,
  } = useEditSection();
  // This component renders only in full-report mode, where the schema is always
  // present (narrative-only reports render NarrativeOnlyEditor instead).
  const jsonSchema = jsonSchemaOrNull as JSONSchema7;
  const schemaMapping = schemaMappingOrNull as Record<string, FieldMapping>;
  const formDataFromStore = useFormDataStore((state) => state.formData);
  // The deleted prop was typed `any` (RJSF data); keep that contract.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formData = formDataFromStore as any;

  const theme = useTheme();
  const organizationId = useOrgId();

  // Navigation state - tracks current section and array indices
  const currentSection = useFormDataStore((state) => state.currentSection);
  const setCurrentSection = useFormDataStore(
    (state) => state.setCurrentSection,
  );

  const [, setHasFormErrors] = useState(false);
  // isNavigating is handled in hook

  // State for page confirmations
  const [pageConfirmations, setPageConfirmations] = useState<{
    [key: number]: boolean;
  }>({});

  const [finalConfirmation, setFinalConfirmation] = useState(false);

  // Track sections modified by AI that need officer attention
  const [sectionsNeedingAttention, setSectionsNeedingAttention] = useState<{
    [key: number]: boolean;
  }>({});

  // Video preview control state
  const [isVideoMinimized, setIsVideoMinimized] = useState(true);

  // References for DOM elements - used for scrolling behavior
  const itemRefs = useRef<HTMLDivElement[]>([]);
  const sectionBarRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);

  // Clear item refs when changing sections
  useEffect(() => {
    itemRefs.current = [];
  }, [currentSection]);

  const handleToggleVideoMinimize = () => {
    setIsVideoMinimized((prev) => !prev);
  };

  // App-level toast notifications (errors, warnings, info)
  const toast = useToast();

  /**
   * @name scrollToTop
   *
   * @description Scrolls to the section progress bar so navigation lands on
   * the nav pills. Skips the scroll entirely when the bar is already at or
   * below the top of the viewport (user is above the anchor already).
   */
  const scrollToTop = () => {
    setTimeout(() => {
      formRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      const target = sectionBarRef.current;
      if (!target) return;
      if (target.getBoundingClientRect().top >= 0) return;
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const {
    sections,
    totalSections,
    generatedUiSchema: propGeneratedUiSchema,
  } = useFormSections(jsonSchema, schemaMapping);

  // Section names for the progress bar: General Info first, then each schema
  // section in order.
  const sectionNames = useMemo(
    () => ["General Info", ...sections],
    [sections],
  );

  const {
    isNavigating,
    handleSectionChangeForward,
    handleSectionChangeBackward,
    handleSidebarItemClick,
  } = useSectionNavigation(
    currentSection,
    totalSections,
    sections,
    setCurrentSection,
    setHasFormErrors,
    scrollToTop,
    handleUserActivity,
  );

  const handleChanges = (e: any) => {
    let updatedFormData = { ...formData };

    if (currentSection === 0) {
      updatedFormData = { ...updatedFormData, ...e.formData };
    } else {
      const sectionKey = sections[currentSection - 1];
      updatedFormData[sectionKey] = e.formData;
    }

    onUpdate(updatedFormData, false);
    handleUserActivity("Edit form data event");
  };

  // Check confirmation status
  const isCurrentPageConfirmed = pageConfirmations[currentSection] === true;
  console.log(
    "Page confirmations:",
    pageConfirmations,
    "Current page confirmed:",
    isCurrentPageConfirmed,
  );
  const areAllPagesConfirmed = (() => {
    for (let i = 0; i < totalSections; i++) {
      if (!pageConfirmations[i]) return false;
    }
    return true;
  })(); // invoke immediately

  const handleCheckBoxChange = (checked: boolean) => {
    setPageConfirmations({
      ...pageConfirmations,
      [currentSection]: checked,
    });

    // Clear attention state when officer confirms the section
    if (checked && sectionsNeedingAttention[currentSection]) {
      setSectionsNeedingAttention((prev) => {
        const updated = { ...prev };
        delete updated[currentSection];
        return updated;
      });
    }

    handleUserActivity(
      `Confirming page ${currentSection} with value ${checked}`,
    );
  };

  // Detect which sections changed after agent modification
  // The snapshot is captured in EditSectionPage before fetchReportData is called
  useEffect(() => {
    const beforeData = formDataSnapshotRef?.current;

    console.log("[Agent Change Detection] useEffect triggered:", {
      hasBeforeData: !!beforeData,
      hasFormData: !!formData,
      hasJsonSchema: !!jsonSchema,
      beforeOtherInvolved: beforeData?.OtherInvolved?.length,
      afterOtherInvolved: formData?.OtherInvolved?.length,
    });

    // Only process if we have a snapshot to compare against
    if (!beforeData || !formData || !jsonSchema) {
      return;
    }

    const changedIndices: number[] = [];

    // Check General Info (section 0) - non-array/object properties
    const oldGeneral = getReportInformationProperties(
      jsonSchema,
      beforeData,
    ).data;
    const newGeneral = getReportInformationProperties(
      jsonSchema,
      formData,
    ).data;
    if (JSON.stringify(oldGeneral) !== JSON.stringify(newGeneral)) {
      changedIndices.push(0);
      console.log("[Agent Change Detection] General Info changed");
    }

    // Check other sections (1+)
    sections.forEach((sectionKey, index) => {
      const oldSection = JSON.stringify(beforeData[sectionKey]);
      const newSection = JSON.stringify(formData[sectionKey]);
      if (oldSection !== newSection) {
        changedIndices.push(index + 1);
        console.log(
          `[Agent Change Detection] Section "${sectionKey}" (index ${index + 1}) changed`,
        );
      }
    });

    console.log(
      "[Agent Change Detection] Total changed sections:",
      changedIndices,
    );

    // Only reset confirmations and mark attention for pages that actually changed
    if (changedIndices.length > 0) {
      // Reset final confirmation - need to re-confirm after reviewing changes
      setFinalConfirmation(false);

      // Mark changed sections as needing attention
      setSectionsNeedingAttention((prev) => {
        const updated = { ...prev };
        changedIndices.forEach((i) => {
          updated[i] = true;
        });
        return updated;
      });

      // Reset confirmations ONLY for changed pages (keep unchanged pages confirmed)
      setPageConfirmations((prev) => {
        console.log(
          "[Agent Change Detection] Resetting confirmations for indices:",
          changedIndices,
          "from:",
          prev,
        );
        const updated = { ...prev };
        changedIndices.forEach((i) => {
          delete updated[i];
        });
        console.log("[Agent Change Detection] New confirmations:", updated);
        return updated;
      });
    }

    // Clear the snapshot after processing
    if (formDataSnapshotRef) {
      formDataSnapshotRef.current = null;
    }
  }, [formData, jsonSchema, sections, formDataSnapshotRef]);

  /**
   * @name handleSubmit
   *
   * @description Handles form submission after validation
   * Triggers onSubmit callback which handles navigation in parent component
   */
  const handleSubmit = async () => {
    handleUserActivity("Submit Report");
    if (!areAllPagesConfirmed || !finalConfirmation) {
      toast.warning(
        "Please confirm all pages and the final confirmation before submitting.",
      );
      return;
    }
    try {
      // onSubmit (parent) runs the single coordinated save+ack and then
      // navigates. A separate onUpdate(formData, true) here would fire a second
      // competing save, so the first ack to arrive resolves the parent's
      // deferred and navigation fires before the real save is confirmed. The
      // store already holds the latest edits (every field change calls
      // onUpdate(_, false)), so no pre-submit store write is needed.
      await onSubmit();
    } catch (error) {
      console.error("Error submitting data:", error);
    }
  };

  /**
   * @name scrollToItem
   *
   * @description Scrolls to a specific item in the array and highlights it briefly
   *
   * @param {number} index - Index of the item to scroll to
   */
  const scrollToItem = (index: number) => {
    const element = itemRefs.current[index];
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      // Add a brief highlight effect
      const originalBorder = element.style.border;
      const originalBoxShadow = element.style.boxShadow;
      element.style.border = "2px solid #1976d2";
      element.style.boxShadow = "0 0 10px rgba(25, 118, 210, 0.5)";

      setTimeout(() => {
        element.style.border = originalBorder;
        element.style.boxShadow = originalBoxShadow;
      }, 1500);
    }
  };

  const formContainerSx = {
    width: "100%",
    "& .MuiFormControl-root": { mb: 2 },
    "& .MuiFormControl-root:has(.MuiRadioGroup-root)": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    "& .MuiFormGroup-root.MuiRadioGroup-root": {
      justifyContent: "center",
    },
    "& .MuiGrid-root": { maxWidth: "100%" },
    "& .MuiOutlinedInput-root": {
      backgroundColor: "background.paper",
    },
    "& .field-object": { mb: 2 },
    "& .field-array": { mb: 3 },
    "& .array-item": {
      p: 2,
      mb: 2,
      border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
      borderRadius: 1,
      backgroundColor: "background.paper",
    },
    "& .MuiFormControlLabel-root": { ml: 0 },
    "& .MuiGrid-container": { alignItems: "flex-start" },
  } as const;

  const renderFormSection = ({
    header,
    badgeNumber,
    schema,
    data,
    uiSchema,
    onChange,
    sectionTitle,
  }: {
    header: string;
    badgeNumber?: number;
    schema: RJSFSchema;
    data: unknown;
    uiSchema: UiSchema;
    onChange: (e: IChangeEvent) => void;
    sectionTitle: string | undefined;
  }) => (
    <>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        {badgeNumber !== undefined && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: theme.palette.primary.main,
              color: "white",
              fontWeight: 700,
              fontSize: "1rem",
              flexShrink: 0,
              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
            }}
          >
            {badgeNumber}
          </Box>
        )}
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {formatSchemaName(header)}
        </Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={0}>
        <Box sx={formContainerSx}>
          <SchemaForm
            schema={schema}
            liveValidate
            formData={data}
            uiSchema={uiSchema}
            onChange={(e) => {
              onChange(e);
              setHasFormErrors((e.errors?.length ?? 0) > 0);
            }}
            autoComplete="on"
            templates={{ ButtonTemplates: { AddButton, RemoveButton } }}
            formContext={{
              itemRefs,
              sectionTitle,
              // The page renders each section under its own numbered header,
              // so suppress LayoutGridField's hardcoded "General Information"
              // root heading to avoid showing the section name twice.
              suppressRootSectionHeading: true,
              liveWebSocket,
              handleUserActivity,
              handleSaveChanges,
              showError: toast.error,
              reportId,
              reportType,
              fetchReportData,
              userProfile,
              chatSendMessage: chatSendMessageRef?.current,
              setShowChat,
              showAIControls: true,
              stationId: organizationId,
            }}
            readonly={!allowEditing || isSaving}
            disabled={!allowEditing || isSaving}
          />
        </Box>
      </Grid>
    </>
  );

  /**
   * @name renderMainContent
   *
   * @description Renders the main content area based on current section.
   * When narrative-only is ON, renders General Information + Narratives stacked
   * inline as a single page (no numbered badges, no section navigation).
   */
  const renderMainContent = () => {
    let data = null;
    let schema = null;
    let header = null;
    let uiSchema = propGeneratedUiSchema;
    if (currentSection === 0) {
      ({ data, schema } = getReportInformationProperties(jsonSchema, formData));
      header = "General Information";
      uiSchema = generateUiSchema(
        schema as RJSFSchema,
        schemaMapping,
        jsonSchema as RJSFSchema,
      );
    } else {
      header = sections[currentSection - 1];
      ({ schema, data } = getObjectSchemaAndData(
        jsonSchema,
        header,
        formData,
        initializeNullArrays,
      ));
      const sectionUiSchema = propGeneratedUiSchema[header];
      if (sectionUiSchema) {
        uiSchema = {
          "ui:submitButtonOptions":
            propGeneratedUiSchema["ui:submitButtonOptions"],
          "ui:options": propGeneratedUiSchema["ui:options"],
          ...sectionUiSchema,
        };
      }
    }

    return (
      <>
        {renderFormSection({
          header: header!,
          badgeNumber: currentSection + 1,
          schema,
          data,
          uiSchema,
          onChange: handleChanges,
          sectionTitle: sections[currentSection - 1],
        })}
        <Box sx={{ mt: 1, textAlign: "center" }}>
          {allowEditing && isReportOwner && (
            <ConfirmationCheckbox
              isConfirmed={isCurrentPageConfirmed}
              isDisabled={isSaving}
              onChange={handleCheckBoxChange}
            />
          )}
        </Box>
      </>
    );
  };

  return (
    <Box sx={{ overscrollBehavior: "contain" }}>
      {/* Video Preview - Always at top */}
      {videoData ? (
        <Box sx={{ mb: 2 }}>
          <VideoPreview
            videoData={videoData}
            isMinimized={isVideoMinimized}
            onToggleMinimize={handleToggleVideoMinimize}
          />
        </Box>
      ) : videoLoadStatus === "loading" ? (
        <Box sx={{ mb: 2 }}>
          <Skeleton
            variant="rectangular"
            height={72}
            sx={{ borderRadius: 2 }}
          />
        </Box>
      ) : (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Video evidence could not be loaded.
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Main Form */}
        <Grid size={{ xs: 12, md: 12 }}>
          <Paper
            sx={{
              p: 3,
              position: "relative",
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
              boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.06)}`,
            }}
          >
            {totalSections > 1 && (
              <Box ref={sectionBarRef} sx={{ scrollMarginTop: 8 }}>
                <SectionProgressBar
                  currentSection={currentSection}
                  totalSections={totalSections}
                  sectionNames={sectionNames}
                  pageConfirmations={pageConfirmations}
                  sectionsNeedingAttention={sectionsNeedingAttention}
                  allowEditing={allowEditing}
                  onSectionClick={handleSidebarItemClick}
                  showWarning={toast.warning}
                />
              </Box>
            )}

            {/* Item selector buttons for array sections */}
            {currentSection > 0 &&
              formData[sections[currentSection - 1]]?.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    mb: 2,
                    flexWrap: "wrap",
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    pb: 2,
                  }}
                >
                  {formData[sections[currentSection - 1]].map(
                    (_: unknown, index: number) => (
                      <Chip
                        key={index}
                        label={`${formatSchemaName(sections[currentSection - 1])} ${index + 1}`.toUpperCase()}
                        onClick={() => scrollToItem(index)}
                        sx={{
                          fontWeight: 600,
                          borderRadius: "20px",
                          "&:hover": {
                            bgcolor: "primary.main",
                            color: "white",
                          },
                        }}
                      />
                    ),
                  )}
                </Box>
              )}

            <Box
              ref={formRef}
              sx={{
                pr: 2,
                boxSizing: "border-box",
                width: "100%",
                position: "relative",
              }}
            >
              <Fade in={isNavigating} timeout={200}>
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                    zIndex: 10,
                    borderRadius: 2,
                  }}
                >
                  <CircularProgress size={40} />
                </Box>
              </Fade>

              <Fade in={!isNavigating} timeout={300}>
                <Box>{renderMainContent()}</Box>
              </Fade>
            </Box>

            <FormNavigation
              currentSection={currentSection}
              totalSections={totalSections}
              isNavigating={isNavigating}
              isSaving={isSaving}
              onPrevious={handleSectionChangeBackward}
              onNext={handleSectionChangeForward}
            />

            <FormSubmitArea
              currentSection={currentSection}
              totalSections={totalSections}
              allowEditing={allowEditing}
              isReportOwner={isReportOwner}
              areAllPagesConfirmed={areAllPagesConfirmed}
              finalConfirmation={finalConfirmation}
              isSaving={isSaving}
              setFinalConfirmation={setFinalConfirmation}
              onSubmit={handleSubmit}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
